<script>
    import { findings, searchFindings } from "./findings.state.svelte.js";
    import userService from "../user/service.svelte.js";
    import { vulnerabilities } from "../vulnerability/vulnerabilities.state.svelte.js";
    import AdvancedSearch from "../../components/powerTable/advanced-search/index.svelte";
    import tagService from "../tag/service.js";
    import BreadCrumNav from "../../components/breadcrum-nav.svelte";
    import List from "./_components/list.svelte";
    import statisticService from "../dashboard/service.js";
    import SeverityTotals from "../vulnerability/_components/severityTotals/v2-severity-totals.svelte";
    import { goto, params } from "@roxi/routify";
    import FindingsBySeverity from "./_components/findings-by-severity.svelte";
    import FindingsByVulKind from "./_components/findings-by-vul-kind.svelte";
    import FindingsByAssetKind from "./_components/findings-by-asset-kind.svelte";
    import FindingsByVulnerability from "./_components/findings-by-vulnerablity.svelte";
    import Spinner from "../../components/spinner.svelte";
    import { format } from "svelte-i18n";
    import { untrack } from "svelte";
    import dayjs from "dayjs";
    import { token } from "../user/user.state.svelte.js";

    // Safely bind router navigation synchronously at root level during setup
    const redirect = $goto;

    let isInitialLoading = $state(true);
    let tags = $state([]);
    let associations = $state([]);
    let users = $state([]);
    let inputs = $state([]);

    // Cleanly derive active search filters directly from reactive URL parameters
    let advancedSearchFilter = $derived($params || {});

    const getTags = async (config = { filter: {} }) => {
        tags = await tagService.search("v2/tag/", config);
        return tags;
    };

    const getTagAssociations = async () => {
        associations = await tagService.search("v2/tag/association/", {
            target_object_type: "Finding",
        });
        return associations;
    };

    const getUsers = async () => {
        if (!token.access_token) return [];
        users = await userService.search();
        return users;
    };

    const buildInputs = (tagsList, assocList, usersList) => {
        return [
            [
                {
                    type: "multiple",
                    name: "vulnerability__level__in",
                    id: "advancedSearchPriority",
                    label: $format("vulnerability.severity.title"),
                    selectAll: true,
                    options: [
                        {
                            name:
                                $format("vulnerability.severity.critical")
                                    .charAt(0)
                                    .toUpperCase() +
                                $format(
                                    "vulnerability.severity.critical",
                                ).slice(1),
                            id: "critical",
                        },
                        {
                            name:
                                $format("vulnerability.severity.high")
                                    .charAt(0)
                                    .toUpperCase() +
                                $format("vulnerability.severity.high").slice(1),
                            id: "high",
                        },
                        {
                            name:
                                $format("vulnerability.severity.medium")
                                    .charAt(0)
                                    .toUpperCase() +
                                $format("vulnerability.severity.medium").slice(
                                    1,
                                ),
                            id: "medium",
                        },
                        {
                            name:
                                $format("vulnerability.severity.low")
                                    .charAt(0)
                                    .toUpperCase() +
                                $format("vulnerability.severity.low").slice(1),
                            id: "low",
                        },
                        {
                            name:
                                $format("vulnerability.severity.info")
                                    .charAt(0)
                                    .toUpperCase() +
                                $format("vulnerability.severity.info").slice(1),
                            id: "info",
                        },
                    ],
                },
                {
                    type: "multiple",
                    name: "status__in",
                    id: "findingStatusMultiple",
                    placeholder: $format("finding.status.title"),
                    label: $format("finding.status.title"),
                    options: [
                        {
                            name: $format("finding.status.open"),
                            id: "open",
                        },
                        {
                            name: $format("finding.status.accepted"),
                            id: "accepted",
                        },
                        {
                            name: $format("finding.status.fixed"),
                            id: "fixed",
                        },
                        {
                            name: $format("finding.status.false_positive"),
                            id: "false_positive",
                        },
                    ],
                },
                {
                    type: "select",
                    name: "user_id",
                    id: "findingUserFilter",
                    label: $format("components.advancedSearch.assigned"),
                    options: usersList,
                },
            ],
            [
                {
                    type: "select",
                    name: "asset_tag_id",
                    id: "findingAssetTagMultiple",
                    label: $format("components.advancedSearch.asset_tag"),
                    options: tagService.filterTagsByTargetObjectType(
                        tagsList,
                        assocList,
                        "Asset",
                    ),
                },
                {
                    type: "select",
                    name: "tag_id",
                    id: "findingTagMultiple",
                    label: $format("components.advancedSearch.finding_tag"),
                    options: tagService.filterTagsByTargetObjectType(
                        tagsList,
                        assocList,
                        "Finding",
                    ),
                },
                {
                    type: "select",
                    name: "vulnerability_tag_id",
                    id: "findingVulnerabilityTagMultiple",
                    label: $format(
                        "components.advancedSearch.vulnerability_tag",
                    ),
                    options: tagService.filterTagsByTargetObjectType(
                        tagsList,
                        assocList,
                        "Vulnerability",
                    ),
                },
            ],
            [
                {
                    type: "multiple",
                    name: "asset__kind__in",
                    id: "assetKindMultiple",
                    label: $format("components.advancedSearch.asset_kind"),
                    options: [
                        {
                            id: "domain",
                            name: $format("asset.kind.domain.singular"),
                        },
                        {
                            id: "hostname",
                            name: "Hostname",
                        },
                        {
                            id: "public_network",
                            name: $format("asset.kind.public_network.singular"),
                        },
                        {
                            id: "private_network",
                            name: $format(
                                "asset.kind.private_network.singular",
                            ),
                        },
                        {
                            id: "public_ip",
                            name: $format("asset.kind.public_ip.singular"),
                        },
                        {
                            id: "private_ip",
                            name: $format("asset.kind.private_ip.singular"),
                        },
                        {
                            id: "ad",
                            name: $format("asset.kind.ad.singular"),
                        },
                        {
                            id: "cloud",
                            name: $format("asset.kind.cloud.singular"),
                        },
                        {
                            id: "software",
                            name: $format("asset.kind.software.singular"),
                        },
                        {
                            id: "endpoint",
                            name: $format("asset.kind.endpoint.singular"),
                        },
                        { id: "email", name: "Email" },
                        {
                            id: "container_image",
                            name: $format(
                                "asset.kind.container_image.singular",
                            ),
                        },
                    ],
                },
                {
                    type: "select",
                    name: "vulnerability__id__ilike",
                    id: "vulTypeFilterFinding",
                    label: $format(
                        "components.advancedSearch.vulnerability_kind",
                    ),
                    options: [
                        {
                            name: $format("vulnerability.kind.ad"),
                            id: "AD-",
                        },
                        {
                            name: $format("vulnerability.kind.asvs"),
                            id: "ASVS-",
                        },
                        {
                            name: $format("vulnerability.kind.cve"),
                            id: "CVE-",
                        },
                        {
                            name: $format("vulnerability.kind.info"),
                            id: "INFO-",
                        },
                        {
                            name: $format("vulnerability.kind.leak"),
                            id: "LEAK-",
                        },
                        {
                            name: $format("vulnerability.kind.nvt"),
                            id: "NVT-",
                        },
                        {
                            name: $format("vulnerability.kind.sve"),
                            id: "SVE-",
                        },
                        {
                            name: $format("vulnerability.kind.eol"),
                            id: "EOL-",
                        },
                        {
                            name: $format("vulnerability.kind.custom"),
                            id: "X-",
                        },
                    ],
                },
            ],
            [
                {
                    type: "date",
                    name: "first_discovery_at",
                    id: "findingDateRange",
                    label: $format("components.advancedSearch.found"),
                },
                {
                    type: "date",
                    name: "last_discovery_at",
                    id: "findingDateRangeSeen",
                    label: $format("components.advancedSearch.seen"),
                },
                {
                    type: "number",
                    name: "sla_days_remaining__lte",
                    id: "slaDaysRemainingLte",
                    label: $format("components.advancedSearch.sla"), // Puedes cambiarlo por un $format si tienes la key de i18n
                    placeholder: "0"
                }
            ],
        ];
    };

    // Unified data loader triggered cleanly by URL parameter changes
    const loadFindingData = async (urlParams = {}) => {
        const [tagsRes, assocRes, usersRes] = await Promise.all([
            getTags(),
            getTagAssociations(),
            getUsers(),
        ]);

        inputs = buildInputs(tagsRes, assocRes, usersRes);

        await searchFindings("v2/finding/", {
            filter: { ...urlParams },
            buildList: {
                tags: { elements: tagsRes, associations: assocRes },
            },
        });

        if (isInitialLoading) isInitialLoading = false;
    };

    // Automatically fetch data on initial load and whenever URL parameters change (Search, Back/Forward arrows)
    $effect(() => {
        const currentUrlParams = JSON.parse(JSON.stringify($params || {}));
        untrack(() => {
            loadFindingData(currentUrlParams);
        });
    });

    const handleAdvancedSearch = (event) => {
        let filters = { ...(event.detail || {}) };

        if (filters.first_discovery_at && filters.first_discovery_at.includes(" - ")) {
            let dates = filters.first_discovery_at.split(" - ");
            filters.first_discovery_at__gte = dayjs(dates[0].replaceAll("/", "-")).format("YYYY-MM-DD") + "T00:00:00.000-03:00";
            filters.first_discovery_at__lte = dayjs(dates[1].replaceAll("/", "-")).format("YYYY-MM-DD") + "T23:59:59.999-03:00";
            delete filters.first_discovery_at;
        }

        if (filters.last_discovery_at && filters.last_discovery_at.includes(" - ")) {
            let dates = filters.last_discovery_at.split(" - ");
            filters.last_discovery_at__gte = dayjs(dates[0].replaceAll("/", "-")).format("YYYY-MM-DD") + "T00:00:00.000-03:00";
            filters.last_discovery_at__lte = dayjs(dates[1].replaceAll("/", "-")).format("YYYY-MM-DD") + "T23:59:59.999-03:00";
            delete filters.last_discovery_at;
        }

        let reqConfig = {};
        for (let [k, v] of Object.entries(filters)) {
            if (v !== "" && v !== null && v !== undefined) {
                if (typeof v === "object" && Array.isArray(v)) {
                    if (v.length > 0 && v[0] !== "") {
                        reqConfig[k] = v.join(",");
                    }
                } else {
                    reqConfig[k] = v;
                }
            }
        }

        // Use Routify v3 navigation to update the URL; the $effect will detect the parameter change and load data
        redirect(".", reqConfig);
    };

    const handleTableRefresh = () => {
        redirect(".");
    };

    /* START GRAPHICS FUNCTIONS */
    const groupFindingsBySeverity = (findingsList) => {
        let totals = {
            critical: 0,
            high: 0,
            medium: 0,
            low: 0,
            info: 0,
        };
        findingsList.forEach((finding) => {
            let levels = Object.keys(totals).join(",");
            if (levels.includes(finding.level)) {
                totals[finding.level]++;
            }
        });
        return totals;
    };

    const groupFindingsByAssetKind = (findingsList) => {
        let totals = {
            domain: 0,
            hostname: 0,
            public_network: 0,
            private_network: 0,
            public_ip: 0,
            private_ip: 0,
            ad: 0,
            cloud: 0,
            software: 0,
            endpoint: 0,
            email: 0,
        };
        findingsList.forEach((finding) => {
            let kinds = Object.keys(totals).join(",");
            if (kinds.includes(finding.asset?.kind)) {
                totals[finding.asset.kind]++;
            }
        });
        return totals;
    };

    const getFindingCount = async () => {
        if (!token.access_token) return {};
        let findingCount = await statisticService.get("finding-count");
        return {
            critical: findingCount.critical,
            high: findingCount.high,
            medium: findingCount.medium,
            low: findingCount.low,
        };
    };

    const groupFindingsByVulnerabilityKind = (findingsList) => {
        let totals = {
            AD: 0,
            ASVS: 0,
            CVE: 0,
            INFO: 0,
            LEAK: 0,
            NVT: 0,
            EOL: 0,
            DMARC: 0,
            SPF: 0,
            M365: 0,
            CODE: 0,
        };
        findingsList.forEach((finding) => {
            let kinds = Object.keys(totals).join(",");
            if (kinds.includes(finding.vul_kind)) {
                totals[finding.vul_kind]++;
            }
        });
        return totals;
    };

    const getVulnerabilityTopFive = (findingsList, vulnerabilitiesList) => {
        const result = findingsList.reduce((r, o) => {
            Object.entries(o).forEach(([key, val]) => {
                if (key === "vulnerability_id") {
                    r[val] = (r[val] || 0) + 1;
                }
            });
            return r;
        }, {});

        let orderedVulCount = Object.entries(result)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5);

        let finalResult = [];
        for (let [vulId, count] of orderedVulCount) {
            let obj = { id: vulId, value: count };
            let vul = vulnerabilitiesList.find((v) => v.id == vulId);
            if (vul) {
                obj["title_es"] = vul.title_es || vul.title;
                obj["title"] = vul.title || vul.title_es;
                finalResult.push(obj);
            }
        }
        return finalResult;
    };

    let findingsBySeverity = $derived.by(() =>
        groupFindingsBySeverity(findings.list.data || []),
    );
    let findingsByAssetKind = $derived.by(() =>
        groupFindingsByAssetKind(findings.list.data || []),
    );
    let findingsByVulnerabilitKind = $derived.by(() =>
        groupFindingsByVulnerabilityKind(findings.list.data || []),
    );
    let vulnerabilityTopFive = $derived.by(() =>
        getVulnerabilityTopFive(findings.list.data || [], vulnerabilities.data || []),
    );
    /* END GRAPHICS FUNCTIONS */
</script>

{#if isInitialLoading}
    <div
        class="d-flex flex-column justify-content-center"
        style="height: 80vh;"
    >
        <Spinner />
    </div>
{:else}
    <section class="content-header">
        <div class="container-fluid">
            <div class="row">
                <div class="col-6">
                    <h1 class="mt-3 mb-3 page-title">
                        <i class="fa-xs fas fa-radiation"></i>
                        {$format(`finding.plural`)}
                    </h1>
                </div>
                <div class="col-6">
                    <BreadCrumNav
                        routes={[{ label: $format(`finding.plural`) }]}
                    />
                </div>
            </div>
        </div>
    </section>
    <section class="content">
        <div class="container-fluid">
            {#await getFindingCount() then severityTotals}
                <SeverityTotals
                    totals={severityTotals}
                    redirectTo={"finding"}
                    title={$format(
                        "page.dashboard.components.findingsBySeverity.title",
                    )}
                />
            {/await}
        </div>
    </section>
    <section class="content">
        <div class="container-fluid">
            <div class="card mt-3 card-secondary card-outline">
                <div class="card-header">
                    <h4 class="card-title no-export">
                        {$format("components.advancedSearch.search")}
                    </h4>
                </div>
                <div class="card-body pe-5">
                    {#key JSON.stringify(advancedSearchFilter)}
                        {#if findings.loading || findings.list.loading}
                            <div class="overlay-wrapper">
                                <div
                                    class="text-center"
                                    class:overlay={findings.loading ||
                                        findings.list.loading}
                                >
                                    <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                                </div>
                            </div>
                        {/if}
                        <AdvancedSearch
                            config={{
                                inputs: inputs,
                                activeFilters: advancedSearchFilter,
                            }}
                            on:advancedSearch={handleAdvancedSearch}
                            on:clearFilter={handleTableRefresh}
                        />
                    {/key}
                </div>
            </div>
        </div>
    </section>
    <section class="content">
        <div class="container-fluid">
            <div class="row">
                <div class="col-12">
                    {#if findings.loading}
                        <div class="overlay-wrapper">
                            <div
                                class="text-center"
                                class:overlay={findings.loading}
                            >
                                <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                            </div>
                        </div>
                    {/if}
                    <div class="d-flex" style="gap: 15px;">
                        <div class="card mt-3" style="flex: 1;">
                            <div class="card-header">
                                <div class="card-title no-export">
                                    {$format(
                                        "page.dashboard.components.findingsBySeverity.title",
                                    )}
                                </div>
                            </div>
                            <div class="card-body">
                                {#if !findings.list.loading}
                                    {#if findings.list.data.length > 0}
                                        {#key findingsBySeverity}
                                            <FindingsBySeverity
                                                findingCount={findingsBySeverity}
                                                {handleAdvancedSearch}
                                                {advancedSearchFilter}
                                            />
                                        {/key}
                                    {:else}
                                        <div
                                            class="chart-has-no-data d-flex flex-column justify-content-center text-center"
                                            style="min-height: 250px;"
                                        >
                                            {$format("results_not_found")}
                                        </div>
                                    {/if}
                                {:else}
                                    <Spinner />
                                {/if}
                            </div>
                        </div>
                        <div class="card mt-3" style="flex: 1;">
                            <div class="card-header">
                                <div class="card-title no-export">
                                    {$format(
                                        "page.vulnerability.top_five_vuls",
                                    )}
                                </div>
                            </div>
                            <div class="card-body">
                                {#if !findings.list.loading}
                                    {#if findings.list.data.length > 0}
                                        {#key vulnerabilityTopFive}
                                            <FindingsByVulnerability
                                                findings={vulnerabilityTopFive}
                                                on:changeLocation={(e) =>
                                                    redirect(`/vulnerability/${e.detail.vulnerability_id}`)}
                                            />
                                        {/key}
                                    {:else}
                                        <div
                                            class="chart-has-no-data d-flex flex-column justify-content-center text-center"
                                            style="min-height: 250px;"
                                        >
                                            {$format("results_not_found")}
                                        </div>
                                    {/if}
                                {:else}
                                    <Spinner />
                                {/if}
                            </div>
                        </div>
                    </div>
                    <div class="d-flex" style="gap: 15px;">
                        <div class="card mt-3" style="flex: 1;">
                            <div class="card-header">
                                <div class="card-title no-export">
                                    {$format(
                                        "finding.components.findings_by_asset_kind",
                                    )}
                                </div>
                            </div>
                            <div class="card-body">
                                {#if !findings.list.loading && !findings.loading}
                                    {#if findings.list.data.length > 0}
                                        {#key findingsByAssetKind}
                                            <FindingsByAssetKind
                                                findingCount={findingsByAssetKind}
                                                {handleAdvancedSearch}
                                                {advancedSearchFilter}
                                            />
                                        {/key}
                                    {:else}
                                        <div
                                            class="chart-has-no-data d-flex flex-column justify-content-center text-center"
                                            style="min-height: 250px;"
                                        >
                                            {$format("results_not_found")}
                                        </div>
                                    {/if}
                                {:else}
                                    <Spinner />
                                {/if}
                            </div>
                        </div>
                        <div class="card mt-3" style="flex: 1;">
                            <div class="card-header">
                                <div class="card-title no-export">
                                    {$format(
                                        "finding.components.findings_by_vul_kind",
                                    )}
                                </div>
                            </div>
                            <div class="card-body">
                                {#if !findings.list.loading}
                                    {#if findings.list.data.length > 0}
                                        {#key findingsByVulnerabilitKind}
                                            <FindingsByVulKind
                                                findingCount={findingsByVulnerabilitKind}
                                                {handleAdvancedSearch}
                                                {advancedSearchFilter}
                                            />
                                        {/key}
                                    {:else}
                                        <div
                                            class="chart-has-no-data d-flex flex-column justify-content-center text-center"
                                            style="min-height: 250px;"
                                        >
                                            {$format("results_not_found")}
                                        </div>
                                    {/if}
                                {:else}
                                    <Spinner />
                                {/if}
                            </div>
                        </div>
                    </div>
                    <List
                        {tags}
                        {associations}
                        parent={"finding"}
                        {users}
                        showRefresh={true}
                        showTitle={false}
                        showActions={true}
                        graphicsData={{
                            findingsBySeverity,
                            findingsByAssetKind,
                            findingsByVulnerabilitKind,
                            vulnerabilityTopFive
                        }}
                    />
                </div>
            </div>
        </div>
    </section>
{/if}

<style>
</style>